import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Package, Save, Star, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { MultipleImageUpload, ProductImage } from '@/components/MultipleImageUpload'
import { apiClient } from '@/services/api'

interface Product {
  id: string
  name: string
  type: string
  description?: string
  materialCost: number
  laborCost: number
  totalCost: number
  suggestedSellingPrice: number
  actualSellingPrice?: number
  profitMargin?: number
  totalLaborHours: number
  imageUrls: string[]
  isActive: boolean
  isFeatured: boolean
  timesReordered: number
  quantityInStock: number
  quantitySold: number
  lastSoldPrice?: number
  lastSoldDate?: string
  customerName?: string
  completedDate: string
  createdAt: string
  notes?: string
}

interface ProductFormData {
  name: string
  description: string
  actualSellingPrice: string
  isFeatured: boolean
  isActive: boolean
  notes: string
}

export const EditProductPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  
  const [product, setProduct] = useState<Product | null>(null)
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    actualSellingPrice: '',
    isFeatured: false,
    isActive: true,
    notes: ''
  })
  const [productImages, setProductImages] = useState<ProductImage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (id) {
      fetchProduct(id)
    }
  }, [id])

  const fetchProduct = async (productId: string) => {
    try {
      setIsLoading(true)
      const productData = await apiClient.get(`/products/${productId}`)
      
      setProduct(productData)
      setFormData({
        name: productData.name || '',
        description: productData.description || '',
        actualSellingPrice: productData.actualSellingPrice?.toString() || '',
        isFeatured: productData.isFeatured || false,
        isActive: productData.isActive !== false, // Default to true if undefined
        notes: productData.notes || ''
      })

      // Fetch product images using the new API, with fallback to old imageUrls
      try {
        const images = await apiClient.get(`/products/${productId}/images`)
        setProductImages(images)
      } catch (error) {
        console.error('Error fetching product images, using fallback:', error)
        // Fallback: Convert old imageUrls to ProductImage format for compatibility
        if (productData.imageUrls && productData.imageUrls.length > 0) {
          const fallbackImages: ProductImage[] = productData.imageUrls.map((url: string, index: number) => ({
            id: `fallback-${index}`,
            imageUrl: url,
            altText: `${productData.name} ${index + 1}`,
            displayOrder: index,
            isDefault: index === 0, // First image is default
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }))
          setProductImages(fallbackImages)
        } else {
          setProductImages([])
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error)
      alert('Error loading product. Please try again.')
      navigate('/products')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof ProductFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleImageUpload = async (files: File[], setFirstAsDefault?: boolean): Promise<ProductImage[]> => {
    if (!id) throw new Error('Product ID is required')
    
    const formData = new FormData()
    files.forEach(file => {
      formData.append('images', file)
    })
    
    if (setFirstAsDefault) {
      formData.append('setFirstAsDefault', 'true')
    }

    const response = await apiClient.post(`/products/${id}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response
  }

  const handleSetDefault = async (imageId: string): Promise<ProductImage> => {
    if (!id) throw new Error('Product ID is required')
    
    // Check if this is a fallback image (from old imageUrls system)
    if (imageId.startsWith('fallback-')) {
      throw new Error('Cannot set fallback images as default. Please upload new images to use this feature.')
    }
    
    const response = await apiClient.put(`/products/${id}/images/${imageId}/default`)
    return response
  }

  const handleRemoveImage = async (imageId: string): Promise<void> => {
    if (!id) throw new Error('Product ID is required')
    
    // Check if this is a fallback image (from old imageUrls system)
    if (imageId.startsWith('fallback-')) {
      throw new Error('Cannot remove fallback images. Please upload new images to replace them.')
    }
    
    await apiClient.delete(`/products/${id}/images/${imageId}`)
  }

  const handleReorderImages = async (imageOrders: Array<{ imageId: string; displayOrder: number }>): Promise<ProductImage[]> => {
    if (!id) throw new Error('Product ID is required')
    
    // Check if any images are fallback images (from old imageUrls system)
    const hasFallbackImages = imageOrders.some(order => order.imageId.startsWith('fallback-'))
    if (hasFallbackImages) {
      throw new Error('Cannot reorder fallback images. Please upload new images to use this feature.')
    }
    
    const response = await apiClient.put(`/products/${id}/images/reorder`, { imageOrders })
    return response
  }

  const handleImagesChange = (images: ProductImage[]) => {
    setProductImages(images)
  }

  const calculateProfitMargin = () => {
    if (!product) return 0
    const actualPrice = parseFloat(formData.actualSellingPrice) || 0
    if (actualPrice <= 0) return 0
    const totalCost = Number(product.totalCost) || 0
    return ((actualPrice - totalCost) / actualPrice) * 100
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) newErrors.name = 'Product name is required'
    if (formData.actualSellingPrice && parseFloat(formData.actualSellingPrice) <= 0) {
      newErrors.actualSellingPrice = 'Selling price must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || !product || !id) return

    setIsSubmitting(true)
    try {
      // Update product data
      const updateData = {
        name: formData.name,
        description: formData.description,
        actualSellingPrice: formData.actualSellingPrice ? parseFloat(formData.actualSellingPrice) : undefined,
        isFeatured: formData.isFeatured,
        isActive: formData.isActive,
        notes: formData.notes
      }

      const result = await apiClient.put(`/products/${id}`, updateData)
      console.log('Product updated successfully:', result)
      
      navigate(`/products/${id}`)
    } catch (error) {
      console.error('Error updating product:', error)
      alert('Error updating product. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-600 dark:text-gray-400">Loading product...</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/products')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Products
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Product Not Found
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              The selected product could not be found.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const profitMargin = calculateProfitMargin()

  return (
    <>
      <Helmet>
        <title>Edit {product.name} - Jewelry Inventory Tracker</title>
      </Helmet>
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/products/${id}`)}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Product
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Edit Product
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Update product information and settings
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Information */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Package className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Product Information
              </h2>
              <Badge variant="secondary" size="sm">
                {product.type}
              </Badge>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Current Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Material Cost:</span>
                    <span className="font-medium">{formatCurrency(Number(product.materialCost))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Labor Cost:</span>
                    <span className="font-medium">{formatCurrency(Number(product.laborCost))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total Hours:</span>
                    <span className="font-medium">{product.totalLaborHours}h</span>
                  </div>
                  <div className="flex justify-between border-t pt-3 font-semibold">
                    <span>Total Cost:</span>
                    <span>{formatCurrency(Number(product.totalCost))}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Suggested Price:</span>
                    <span className="font-semibold">{formatCurrency(Number(product.suggestedSellingPrice))}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Inventory Status</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">In Stock:</span>
                    <span className="font-medium">{product.quantityInStock} units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total Sold:</span>
                    <span className="font-medium">{product.quantitySold} units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Times Reordered:</span>
                    <span className="font-medium">{product.timesReordered}</span>
                  </div>
                  {product.customerName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Customer:</span>
                      <span className="font-medium">{product.customerName}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Editable Fields */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-yellow-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Product Settings
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product Name *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    handleInputChange('name', e.target.value)
                  }
                  placeholder="Product name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Actual Selling Price
                </label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.actualSellingPrice}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    handleInputChange('actualSellingPrice', e.target.value)
                  }
                  placeholder={Number(product.suggestedSellingPrice).toFixed(2)}
                  className={errors.actualSellingPrice ? 'border-red-500' : ''}
                />
                {errors.actualSellingPrice && (
                  <p className="text-red-500 text-sm mt-1">{errors.actualSellingPrice}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  Suggested: {formatCurrency(Number(product.suggestedSellingPrice))}
                </p>
              </div>
            </div>

            {formData.actualSellingPrice && parseFloat(formData.actualSellingPrice) > 0 && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-green-900 dark:text-green-100">
                    Profit Margin:
                  </span>
                  <span className="text-xl font-bold text-green-900 dark:text-green-100">
                    {profitMargin.toFixed(1)}%
                  </span>
                </div>
                <div className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Profit: {formatCurrency(parseFloat(formData.actualSellingPrice) - Number(product.totalCost))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                  handleInputChange('description', e.target.value)
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Describe this product for your catalog..."
              />
            </div>

            <div className="mt-6 flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Featured Product
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                  {formData.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  Active Product
                </span>
              </label>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => 
                  handleInputChange('notes', e.target.value)
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Any additional notes about this product..."
              />
            </div>
          </Card>

          {/* Image Management */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Package className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Product Images
              </h2>
            </div>
            
            <MultipleImageUpload
              productId={id}
              existingImages={productImages}
              onImagesChange={handleImagesChange}
              onUpload={handleImageUpload}
              onSetDefault={handleSetDefault}
              onRemoveImage={handleRemoveImage}
              onReorderImages={handleReorderImages}
              maxImages={10}
              maxFileSize={10}
              disabled={isSubmitting}
            />
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/products/${id}`)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              leftIcon={isSubmitting ? undefined : <Save className="w-4 h-4" />}
            >
              {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}